/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/function-component-definition */
import { Query } from "@apollo/client/react/components";
import { CompanyContainer, CompanyContentContainer } from "components/company/v3/CompanyContainers";
import RootCompanyProxy from "components/company/v3/context/RootCompanyProxy";
import useRootIssuer from "components/company/v3/context/hooks/useRootIssuer";
import CompanyNavbarLayout from "components/company/v3/layouts/CompanyNavbarLayout";
import { CompanyContextEnum } from "components/company/v3/menu/enums";
import IndicatorSnapshot from "components/company/valuation/IndicatorSnapshot";
import SheetValuationHistory from "components/company/valuation/SheetValuationHistory";
import getTabName from "components/company/valuation/getTabName";
import CompanyPageCta from "components/conversion/CompanyPageCta";
import NotFound from "components/errors/NotFound";
import IssuerHeaderByTickerSymbol from "components/issuers/IssuerHeaderByTickerSymbol";
import NavbarLayout from "components/layouts/NavbarLayout";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import FundamenteiMetadata from "components/seo/FundamenteiMetadata";
import { Tabs, TabsList, TabsTrigger } from "components/ui/Tabs";
import classNames from "components/ui/classNames";
import isValid from "date-fns/isValid";
import lightFormat from "date-fns/lightFormat";
import max from "date-fns/max";
import parseISO from "date-fns/parseISO";
import { useAmplitude } from "lib/amplitude/Amplitude";
import useSelfie from "lib/hooks/useSelfie";
import SetHttpStatusCode from "lib/http/SetHttpStatusCode";
import IssuerByTickerSymbolQuery from "lib/queries/IssuerByTickerSymbolQuery";
import ValuationWallForTickerSymbolQuery from "lib/queries/ValuationWallForTickerSymbolQuery";
import type {
  IIssuerByTickerSymbolQuery,
  IIssuerByTickerSymbolQueryVariables,
} from "lib/queries/__generated__/IssuerByTickerSymbolQuery.generated";
import type {
  IValuationWallForTickerSymbolQuery,
  IValuationWallForTickerSymbolQueryVariables,
} from "lib/queries/__generated__/ValuationWallForTickerSymbolQuery.generated";
import getBaseUrl from "lib/utils/getBaseUrl";
import {
  CandlestickChartIcon,
  CircleDollarSignIcon,
  HandCoinsIcon,
  PercentIcon,
  RocketIcon,
  StarsIcon,
  TriangleAlertIcon,
} from "lucide-react";
import type { NextPage } from "next";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import { parseAsStringLiteral, useQueryState } from "nuqs";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { IValuationIndicator } from "types/graphqlTypes";
import { ISecurityCountry, IValuationIndicatorCategory, IValuationIndicatorVerbiageLanguage } from "types/graphqlTypes";

interface IValuationByTickerSymbolProps {
  tickerSymbol: string | string[] | undefined;
  country: string | string[] | undefined;
}

const ValuationByTickerSymbol: NextPage<IValuationByTickerSymbolProps> = ({ tickerSymbol, country }) => {
  const intl = useIntl();
  const [isIndicator, setIsIndicator] = useState<null | IValuationIndicator>(null);
  const [tab] = useQueryState(
    "tab",
    parseAsStringLiteral([
      IValuationIndicatorCategory.Valuation,
      IValuationIndicatorCategory.Earnings,
      IValuationIndicatorCategory.Growth,
      IValuationIndicatorCategory.Leverage,
      IValuationIndicatorCategory.MarketCapitalization,
      IValuationIndicatorCategory.Profitability,
      "MY_INDICATORS",
    ]).withDefault(IValuationIndicatorCategory.Valuation),
  );

  const viewer = useSelfie();
  const router = useRouter();
  const amplitude = useAmplitude();
  const normalizedTickerSymbol = Array.isArray(tickerSymbol) ? tickerSymbol[0] : tickerSymbol || "";
  const securityCountry = (
    Array.isArray(country) ? country[0].toUpperCase() : (country || "").toUpperCase()
  ) as ISecurityCountry;

  if (
    !tickerSymbol ||
    tickerSymbol.length > 8 ||
    /^([0-9]+)$/.test(normalizedTickerSymbol) ||
    ![ISecurityCountry.Br, ISecurityCountry.Us].includes(securityCountry)
  ) {
    return (
      <>
        <SetHttpStatusCode httpStatus={404} />
        <NotFound />
      </>
    );
  }

  return (
    <NavbarLayout>
      <FundamenteiMetadata title={`${normalizedTickerSymbol.toUpperCase()} - Valuation | Fundamentei`} />

      {tab === IValuationIndicatorCategory.Valuation && (
        <Head>
          <link
            key="canonical"
            rel="canonical"
            href={`${getBaseUrl()}${router.asPath.replace("?tab=VALUATION", "")}`}
          />
        </Head>
      )}

      <Query<IIssuerByTickerSymbolQuery, IIssuerByTickerSymbolQueryVariables>
        query={IssuerByTickerSymbolQuery}
        variables={{
          tickerSymbol: normalizedTickerSymbol,
          country: securityCountry,
        }}
      >
        {({ data, loading }) => {
          if (loading) {
            return (
              <div className="flex flex-col gap-6 pb-6">
                <LoadingSkeleton count={1} className="h-16 w-full rounded-none" />

                <CompanyContainer>
                  <LoadingSkeleton count={1} className="h-96" />
                </CompanyContainer>
              </div>
            );
          }
          if (!data || !data.issuerByTickerSymbol) {
            return (
              <>
                <SetHttpStatusCode httpStatus={404} />
                <NotFound />
              </>
            );
          }

          const issuer = data.issuerByTickerSymbol;
          const verbiageLanguage = router.locale?.startsWith("en")
            ? IValuationIndicatorVerbiageLanguage.EnUs
            : IValuationIndicatorVerbiageLanguage.PtBr;

          return (
            <RootCompanyProxy issuer={issuer}>
              <CompanyNavbarLayout
                tickerSymbol={normalizedTickerSymbol.toUpperCase()}
                context={
                  securityCountry === ISecurityCountry.Br
                    ? CompanyContextEnum.BrazilianCompany
                    : CompanyContextEnum.AmericanCompany
                }
              >
                <Query<IValuationWallForTickerSymbolQuery, IValuationWallForTickerSymbolQueryVariables>
                  query={ValuationWallForTickerSymbolQuery}
                  variables={{
                    tickerSymbol: normalizedTickerSymbol,
                    securityCountry,
                    verbiageLanguage,
                  }}
                >
                  {({ data, loading }) => {
                    if (loading || !data || !data.valuationWallForTickerSymbol) {
                      return null;
                    }

                    const nodes = data.valuationWallForTickerSymbol.filter((node) => {
                      if (tab === "MY_INDICATORS") {
                        return viewer && viewer.settings.bookmarkedIndicators.includes(node.indicator);
                      }
                      return node.category === tab;
                    });
                    const mostRecentAsOfDate = max(nodes.map((node) => parseISO(node.asOfDate)));

                    return (
                      <CompanyContainer className="xl:h-full">
                        {!viewer && (
                          <IssuerHeaderByTickerSymbol
                            country={securityCountry}
                            tickerSymbol={normalizedTickerSymbol}
                            title={getTabName(intl, tab)}
                          />
                        )}

                        <CompanyContentContainer>
                          <ValuationHead tab={tab} tickerSymbol={normalizedTickerSymbol.toUpperCase()} />
                          <Tabs
                            defaultValue={IValuationIndicatorCategory.Valuation}
                            value={tab}
                            onValueChange={() => {}}
                            className="w-full"
                          >
                            <ValuationTabs
                              availableCategories={data.valuationWallForTickerSymbol.map((node) => node.category)}
                            />

                            <div className="flex h-full w-full p-4">
                              {nodes.length > 0 && (
                                <div className="space-y-4">
                                  <div
                                    className={classNames(
                                      "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4",
                                      "divide divide-gray-100",
                                    )}
                                  >
                                    {nodes.map((node) => {
                                      return (
                                        <SheetValuationHistory
                                          key={node.indicator}
                                          verbiageLanguage={verbiageLanguage}
                                          securityCountry={securityCountry}
                                          tickerSymbol={normalizedTickerSymbol}
                                          indicator={node.indicator}
                                          tab={tab}
                                          indicators={nodes}
                                          viewerCanSeeHistory={node.viewerCanSeeHistory}
                                          setIsOpen={(open) => {
                                            if (!node.viewerCanSeeHistory) {
                                              return;
                                            }

                                            setIsIndicator(open ? node.indicator : null);
                                          }}
                                          onSetIndicator={setIsIndicator}
                                          isOpen={isIndicator === node.indicator}
                                        >
                                          <div>
                                            <IndicatorSnapshot
                                              indicator={node.indicator}
                                              abbreviation={node.verbiage.abbreviation}
                                              description={node.verbiage.description}
                                              category={tab === "MY_INDICATORS" ? node.category : undefined}
                                              originalValue={node.value}
                                              verbiageLanguage={verbiageLanguage}
                                              formattedValue={node.formattedValue}
                                              isBookmarked={
                                                viewer
                                                  ? viewer.settings.bookmarkedIndicators.includes(node.indicator)
                                                  : false
                                              }
                                              viewerCanBookmark={viewer !== null}
                                              isAdjusted={String(node.indicator).includes("NORMALIZED")}
                                              viewerCanSeeHistory={node.viewerCanSeeHistory}
                                              onToggleActive={() => {
                                                amplitude.logEvent("Viewed a valuation indicator", {
                                                  tickerSymbol: normalizedTickerSymbol.toUpperCase(),
                                                  indicator: node.indicator,
                                                  abbreviation: node.verbiage.abbreviation,
                                                  category: node.category,
                                                });

                                                if (!node.viewerCanSeeHistory) {
                                                  return;
                                                }

                                                window.history.pushState(
                                                  null,
                                                  "",
                                                  `/${securityCountry.toLocaleLowerCase()}/${tickerSymbol}/valuation/${
                                                    node.indicator
                                                  }?tab=${tab}`,
                                                );

                                                setIsIndicator((previous) => {
                                                  if (node.indicator === previous) {
                                                    return null;
                                                  }

                                                  return node.indicator;
                                                });
                                              }}
                                            />
                                          </div>
                                        </SheetValuationHistory>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {nodes.length === 0 && tab === "MY_INDICATORS" && (
                                <div className="flex w-full flex-col items-center justify-center space-y-1 p-6">
                                  <h4 className="text-2xl font-bold text-slate-700">
                                    <FormattedMessage defaultMessage="Nenhum indicador encontrado." id="BE9tbp" />
                                  </h4>
                                  <p className="max-w-xl text-base font-normal tracking-wide text-gray-500">
                                    <FormattedMessage
                                      defaultMessage="Para simplificar sua análise, aqui você encontra todos os indicadores que você salvou. Volte aqui quando você salvar algum—é uma aba dedicada apenas para você :)"
                                      id="+HHJE+"
                                    />
                                  </p>
                                </div>
                              )}

                              {nodes.length === 0 && tab !== "MY_INDICATORS" && (
                                <div className="flex w-full flex-col items-center justify-center space-y-1 p-6">
                                  <h4 className="text-2xl font-bold text-slate-700">
                                    <FormattedMessage defaultMessage="Nenhum indicador encontrado." id="BE9tbp" />
                                  </h4>
                                  <p className="max-w-xl text-base font-normal tracking-wide text-gray-500">
                                    <FormattedMessage
                                      defaultMessage="Não encontramos nenhum indicador para <strong>{normalizedTickerSymbol}</strong>.
                          Verifique se o ticker foi digitado corretamente—ou se o mesmo está listado. Caso contrário,
                          não possuimos dados no momento para tal ativo :("
                                      id="HdEBa0"
                                      values={{
                                        normalizedTickerSymbol: normalizedTickerSymbol.toUpperCase(),
                                        strong: (text) => <strong>{text}</strong>,
                                      }}
                                    />
                                  </p>
                                </div>
                              )}
                            </div>
                          </Tabs>
                        </CompanyContentContainer>
                        {isValid(mostRecentAsOfDate) && (
                          <div className="flex items-center justify-end gap-x-2">
                            <time
                              dateTime={lightFormat(mostRecentAsOfDate, "yyyy-MM-dd")}
                              className="inline-flex rounded-full px-1.5 py-0.5 text-sm font-medium text-gray-700"
                            >
                              <FormattedMessage
                                defaultMessage="Última atualização em: {date}"
                                id="uKKIQJ"
                                values={{
                                  date: intl.formatDate(mostRecentAsOfDate, {
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric",
                                  }),
                                }}
                              />
                            </time>
                          </div>
                        )}
                        {!viewer?.isPremium && <CompanyPageCta displayOrigin="Valuation" />}
                      </CompanyContainer>
                    );
                  }}
                </Query>
              </CompanyNavbarLayout>
            </RootCompanyProxy>
          );
        }}
      </Query>
    </NavbarLayout>
  );
};

ValuationByTickerSymbol.getInitialProps = async ({ query }) => ({
  tickerSymbol: Array.isArray(query.tickerSymbol) ? query.tickerSymbol[0] : query.tickerSymbol,
  country: Array.isArray(query.country) ? query.country[0] : query.country,
});

export default ValuationByTickerSymbol;

type TValuationTab = `${IValuationIndicatorCategory}` | "MY_INDICATORS";

interface ValuationTabsProps {
  availableCategories: IValuationIndicatorCategory[];
}

function ValuationTabs({ availableCategories }: ValuationTabsProps) {
  const intl = useIntl();
  const router = useRouter();
  const viewer = useSelfie();

  const tabs: {
    value: TValuationTab;
    label: string;
    icon: React.ReactElement;
  }[] = [
    {
      icon: <CircleDollarSignIcon className="inline-flex" size={16} strokeWidth={2} />,
      value: IValuationIndicatorCategory.Valuation,
      label: getTabName(intl, IValuationIndicatorCategory.Valuation),
    },
    {
      icon: <CandlestickChartIcon className="inline-flex" size={16} strokeWidth={2} />,
      value: IValuationIndicatorCategory.MarketCapitalization,
      label: getTabName(intl, IValuationIndicatorCategory.MarketCapitalization),
    },
    {
      icon: <HandCoinsIcon className="inline-flex" size={16} strokeWidth={2} />,
      value: IValuationIndicatorCategory.Earnings,
      label: getTabName(intl, IValuationIndicatorCategory.Earnings),
    },
    {
      icon: <PercentIcon className="inline-flex" size={16} strokeWidth={2} />,
      value: IValuationIndicatorCategory.Profitability,
      label: getTabName(intl, IValuationIndicatorCategory.Profitability),
    },
    {
      icon: <RocketIcon className="inline-flex" size={16} strokeWidth={2} />,
      value: IValuationIndicatorCategory.Growth,
      label: getTabName(intl, IValuationIndicatorCategory.Growth),
    },
    {
      icon: <TriangleAlertIcon className="inline-flex" size={16} strokeWidth={2} />,
      value: IValuationIndicatorCategory.Leverage,
      label: getTabName(intl, IValuationIndicatorCategory.Leverage),
    },
  ];
  if (viewer) {
    tabs.push({
      icon: <StarsIcon className="inline-flex" size={16} strokeWidth={2} />,
      value: "MY_INDICATORS",
      label: getTabName(intl, "MY_INDICATORS"),
    });
  }

  const availableTabs = tabs.filter((tab) => {
    if (tab.value === "MY_INDICATORS") {
      return true;
    }
    return availableCategories.includes(tab.value as IValuationIndicatorCategory);
  });

  return (
    <TabsList
      className={classNames(
        "flex w-full flex-1 justify-start divide-x overflow-x-auto rounded-b-none rounded-t-xl p-0",
        "bg-slate-50",
        "dark:divide-slate-950 dark:border-slate-900 dark:bg-slate-800",
        "scrollbar-none",
      )}
    >
      {availableTabs.map((tab) => {
        return (
          <TabsTrigger
            key={tab.value}
            value={tab.value}
            asChild={true}
            className={classNames(
              "h-28 min-w-32 justify-center overflow-hidden rounded-none md:h-fit md:w-full",
              "border-b border-b-transparent px-2",
              "border-b-slate-200 dark:border-b-slate-950",
              "group",
            )}
          >
            <Link
              href={{
                pathname: router.pathname,
                query: {
                  ...router.query,
                  tab: tab.value,
                },
              }}
            >
              <div className="flex flex-col items-center gap-2 truncate px-1.5 py-1 text-left md:flex-row">
                <div
                  className={classNames(
                    "flex items-center justify-center rounded-full p-2 squircle",
                    "bg-slate-50 dark:bg-indigo-300/10 dark:text-indigo-200",
                    "group-data-[state=active]:dark:bg-indigo-300/20 group-data-[state=active]:dark:text-indigo-100",
                    "group-data-[state=active]:bg-indigo-50/50 group-data-[state=active]:text-indigo-600",
                  )}
                >
                  {tab.icon}
                </div>
                <span
                  className={classNames(
                    "line-clamp-2 max-w-28 whitespace-pre-wrap text-center leading-6 lg:max-w-none lg:whitespace-nowrap lg:text-left",
                    "text-slate-500 group-data-[state=active]:font-bold",
                    "group-data-[state=active]:text-slate-600 group-data-[state=active]:dark:text-slate-200",
                  )}
                >
                  {tab.label}
                </span>
              </div>
            </Link>
          </TabsTrigger>
        );
      })}
    </TabsList>
  );
}

interface ValuationHeadProps {
  tab: TValuationTab;
  tickerSymbol: string;
}

function ValuationHead({ tab, tickerSymbol }: ValuationHeadProps) {
  const gitShaShort = process.env.GIT_SHA_SHORT || "1";

  const intl = useIntl();
  const issuer = useRootIssuer();

  const languageCode = intl.locale === "pt-BR" ? "pt" : "en";

  if (issuer.__typename === "BrazilianCompanyIssuer") {
    const imageUrl = `${getBaseUrl()}/og/issuer/${tickerSymbol.toLowerCase()}?locale=${languageCode}&ns=br&valuation=true&v=${gitShaShort}`;
    return (
      <FundamenteiMetadata
        title={`${tickerSymbol} - ${getTabName(intl, tab)} | Fundamentei`}
        description={intl.formatMessage(
          {
            defaultMessage: `{tickerSymbol} - {issuerName}: Principais indicadores de valuation (P/L, P/VPA, EBITDA, ROE), CAGR, Alavancagem (D/PL, Quick Ratio, Arrendamento) e muito mais!`,
            id: "KRUsVx",
          },
          {
            tickerSymbol,
            issuerName: issuer.name,
          },
        )}
        openGraph={{
          images: [
            {
              url: imageUrl,
              width: 1200,
              height: 630,
              alt: `${tickerSymbol} - ${issuer.name}`,
            },
          ],
        }}
      />
    );
  }
  if (issuer.__typename === "AmericanCompanyIssuer") {
    const imageUrl = `${getBaseUrl()}/og/issuer/${tickerSymbol.toLowerCase()}?locale=${languageCode}&ns=us&valuation=true&v=${gitShaShort}`;
    return (
      <FundamenteiMetadata
        title={`${tickerSymbol} - ${getTabName(intl, tab)} | Fundamentei`}
        description={intl.formatMessage(
          {
            defaultMessage: `{tickerSymbol} - {issuerName}: Principais indicadores de valuation (P/L, P/VPA, EBITDA, ROE), CAGR, Alavancagem (D/PL, Quick Ratio, Arrendamento) e muito mais!`,
            id: "KRUsVx",
          },
          {
            tickerSymbol,
            issuerName: issuer.name,
          },
        )}
        openGraph={{
          images: [
            {
              url: imageUrl,
              width: 1200,
              height: 630,
              alt: `${tickerSymbol} - ${issuer.name}`,
            },
          ],
        }}
      />
    );
  }
  return null;
}
